import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ActionBar, Container, PageHeader, SettingRow } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Checkbox,
  Input,
  MultiSelect,
  Switch,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useServiceEmergencyMitigation,
  useServiceEmergencyStaticFiles,
  useUpdateServiceEmergencyMitigation,
  useServiceAdvancedEnabledSites,
  emptyEmergencyStrategy,
  EMERGENCY_SCHEDULING,
  EMERGENCY_ACTIONS,
  EMERGENCY_DAYS,
  type EmergencyConfig,
  type EmergencyMitigation,
  type EmergencyStrategy,
  type EmergencyStaticFile,
} from '@/api/waap';
import { DateTimeRangePicker } from '@/components/date-time-range-picker/DateTimeRangePicker';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './EmergencyMitigationTab.module.css';

type StrategyKey = keyof EmergencyConfig;

interface StrategyMeta {
  key: StrategyKey;
  title: string;
  description: string;
  showAction: boolean;
  showException: boolean;
}

const STRATEGIES: StrategyMeta[] = [
  {
    key: 'kill_switch',
    title: 'Kill Switch',
    description: 'One-stop switch to block all requests to websites.',
    showAction: true,
    showException: false,
  },
  {
    key: 'read_only_website',
    title: 'Read-Only Website',
    description:
      'Once enabled, only requests with HTTP GET and HEAD method can visit the website. Requests with other HTTP methods will receive a 400 error code.',
    showAction: false,
    showException: false,
  },
  {
    key: 'static_website',
    title: 'Static Website',
    description:
      'Once enabled, only allow requests to visit static HTML pages and static files. Other requests will receive a 400 error code.',
    showAction: false,
    showException: true,
  },
];

const pad = (n: number) => String(n).padStart(2, '0');

/** "HH:MM:SS" or "HH:MM" → "HH:MM:SS". */
function normTime(v: string): string {
  if (!v) return '00:00:00';
  return v.length === 5 ? `${v}:00` : v;
}

/** UTC ISO → local "HH:MM:SS" (for the Periodic <input type="time">). */
function isoToLocalTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** Current local "HH:MM:SS" for the repeat window start(0)/end(1). The schedule
 *  is stored in UTC (the portal persists timer_repeat as UTC ISO + the matching
 *  UTC timer_repeat_start/end time-of-day); the UI shows it in the operator's
 *  local time, like the vendor portal. Prefers the full ISO, falls back to the
 *  UTC time-of-day. */
function repeatLocalTime(s: EmergencyStrategy, which: 0 | 1): string {
  const iso = s.timer_repeat?.[which];
  if (iso) return isoToLocalTime(iso);
  const t = which === 0 ? s.timer_repeat_start : s.timer_repeat_end;
  return t ? isoToLocalTime(`1970-01-01T${normTime(t)}.000Z`) : '';
}

/** Local "HH:MM:SS" (from the time input) → UTC { iso, time } for storage. */
function localTimeToUtc(localTime: string): { iso: string; time: string } {
  const [hh, mm, ss] = normTime(localTime).split(':').map(Number);
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hh, mm, ss || 0);
  return {
    iso: d.toISOString(),
    time: `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`,
  };
}

/**
 * Emergency Mitigation — account-level "break glass" config. Three sub-strategies
 * (Kill Switch / Read-Only / Static Website), each toggled on with a schedule and
 * the websites it applies to. Mirrors the SwiftFederation portal, translated to
 * console conventions. Whole-object read-modify-write: the page seeds from GET and
 * PUTs the whole object back on Save.
 */
/** A blank emergency-mitigation object for the "no config yet" case (vendor
 *  returns null). Strategy ids are the vendor's fixed ids: Kill Switch 901,
 *  Read-Only Website 902, Static Website 903. */
function defaultEmergencyEnvelope(): EmergencyMitigation {
  return {
    id: 0,
    type: 1,
    created: 0,
    updated: 0,
    status: 0,
    customer_id: 0,
    config: {
      kill_switch: emptyEmergencyStrategy(901),
      read_only_website: emptyEmergencyStrategy(902),
      static_website: emptyEmergencyStrategy(903),
    },
  };
}

export function EmergencyMitigationTab() {
  // Account-level "break glass" config — resolves from the service, so it works
  // with zero websites (the vendor customer is resolved server-side).
  const { serviceId } = useParams<{ serviceId: string }>();
  const { show } = useToast();

  // "Apply to" lists the account's protection-enabled sites (GET /sites/enabled),
  // not just the DB-registered domains. sites are stored by domain name.
  const { data: enabledSites = [] } = useServiceAdvancedEnabledSites(serviceId);
  const websites = useMemo(
    () => enabledSites.map((s) => ({ domainId: String(s.id), domain: s.domain })),
    [enabledSites],
  );

  const { data, isLoading } = useServiceEmergencyMitigation(serviceId);
  const { data: staticFiles = [] } = useServiceEmergencyStaticFiles(serviceId);

  const [envelope, setEnvelope] = useState<EmergencyMitigation | null>(null);
  // The vendor returns null when no emergency config exists yet; the portal still
  // renders the three strategies (Kill Switch 901 / Read-Only 902 / Static 903) as
  // an unsaved default form. Seed that so the page isn't stuck on the skeleton.
  useEffect(() => {
    if (data) setEnvelope(data);
    else if (data === null && !isLoading) setEnvelope(defaultEmergencyEnvelope());
  }, [data, isLoading]);

  const update = useUpdateServiceEmergencyMitigation(serviceId ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'emergency_mitigation', 'edit');
      show({ tone: 'success', title: 'Emergency mitigation saved' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });

  const setStrategy = (key: StrategyKey, patch: Partial<EmergencyStrategy>) =>
    setEnvelope((env) =>
      env ? { ...env, config: { ...env.config, [key]: { ...env.config[key], ...patch } } } : env,
    );

  // Build the exact write shape the portal sends. CREATE (no config yet) sends id /
  // customer_id as EMPTY STRINGS and omits the read-only server fields; UPDATE
  // round-trips the real id / customer_id / created / updated / status from GET.
  // Each strategy echoes service_ids (server re-derives from sites) and carries
  // timer_once / timer_repeat only for the schedule type that uses them.
  const save = () => {
    if (!envelope) return;
    const strat = (s: EmergencyStrategy, withPaths = false): Record<string, unknown> => {
      const out: Record<string, unknown> = {
        id: s.id,
        enable: s.enable,
        action_list: s.action_list,
        timer_type: s.timer_type,
        timer_start: s.timer_start || Date.now(),
        timer_end: s.timer_end || Date.now(),
        timer_repeat_days: s.timer_repeat_days,
        timer_repeat_start: s.timer_repeat_start,
        timer_repeat_end: s.timer_repeat_end,
        sites: s.sites,
        service_ids: s.service_ids ?? [],
      };
      if (s.timer_type === 'once' && s.timer_once) out.timer_once = s.timer_once;
      if (s.timer_type === 'repeat' && s.timer_repeat) out.timer_repeat = s.timer_repeat;
      if (withPaths) {
        out.paths = s.paths ?? [];
        if (s.exception_path) out.exception_path = s.exception_path;
      }
      return out;
    };
    const config = {
      kill_switch: strat(envelope.config.kill_switch),
      read_only_website: strat(envelope.config.read_only_website),
      static_website: strat(envelope.config.static_website, true),
    };
    const isNew = !envelope.id;
    const payload: Record<string, unknown> = isNew
      ? { id: '', customer_id: '', type: envelope.type || 1, config }
      : {
          id: envelope.id,
          customer_id: envelope.customer_id,
          type: envelope.type || 1,
          config,
          created: envelope.created,
          updated: envelope.updated,
          status: envelope.status,
        };
    update.mutate(payload as unknown as EmergencyMitigation);
  };

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<WaapConsoleBreadcrumbs trail={[{ label: 'Emergency Mitigation' }]} />}
        title="Emergency Mitigation"
        description="Emergency mitigation provides defense features to have a quick countermeasure towards ongoing threats or threats under certain business scenarios."
      />

      {isLoading || !envelope ? (
        <div className={styles.skeletonList}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className={styles.skeletonRow} />
          ))}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {STRATEGIES.map((meta) => (
            <StrategyCard
              key={meta.key}
              meta={meta}
              strategy={envelope.config[meta.key] ?? emptyEmergencyStrategy(0)}
              websites={websites}
              staticFiles={staticFiles}
              onChange={(patch) => setStrategy(meta.key, patch)}
            />
          ))}

          <ActionBar
            end={
              <Button disabled={update.isPending} onClick={save}>
                {update.isPending ? 'Saving…' : 'Save'}
              </Button>
            }
          />
        </div>
      )}
    </Container>
  );
}

function StrategyCard({
  meta,
  strategy: s,
  websites,
  staticFiles,
  onChange,
}: {
  meta: StrategyMeta;
  strategy: EmergencyStrategy;
  websites: { domainId: string; domain: string }[];
  staticFiles: EmergencyStaticFile[];
  onChange: (patch: Partial<EmergencyStrategy>) => void;
}) {
  const action = s.action_list[0]?.action ?? 'block_code';
  const actionValue = s.action_list[0]?.action_value;
  const daySet = useMemo(() => new Set(s.timer_repeat_days), [s.timer_repeat_days]);

  // The picker emits ISO datetimes for both endpoints; mirror them into timer_once
  // and the epoch-ms timer_start / timer_end the vendor keys the window by.
  const setOnceRange = (r: { from: string; to: string }) => {
    onChange({
      timer_once: [r.from, r.to],
      timer_start: r.from ? new Date(r.from).getTime() : s.timer_start,
      timer_end: r.to ? new Date(r.to).getTime() : s.timer_end,
    });
  };

  const setRepeatTime = (which: 'start' | 'end', val: string) => {
    // val is local time from the input; persist as UTC (ISO + UTC time-of-day),
    // mirroring how the portal stores the repeat window.
    const localStart = which === 'start' ? val : repeatLocalTime(s, 0);
    const localEnd = which === 'end' ? val : repeatLocalTime(s, 1);
    const start = localTimeToUtc(localStart);
    const end = localTimeToUtc(localEnd);
    onChange({
      timer_repeat_start: start.time,
      timer_repeat_end: end.time,
      timer_repeat: [start.iso, end.iso],
    });
  };

  const toggleDay = (idx: number) =>
    onChange({
      timer_repeat_days: daySet.has(idx)
        ? s.timer_repeat_days.filter((d) => d !== idx)
        : [...s.timer_repeat_days, idx],
    });

  const setAction = (a: string) => {
    if (a === 'response_html') {
      const file = typeof actionValue === 'string' ? actionValue : staticFiles[0]?.name ?? '';
      onChange({ action_list: [{ delay: 0, action: 'response_html', weight: 1, action_value: file }] });
    } else {
      onChange({ action_list: [{ delay: 0, action: 'block_code', weight: 1, action_value: 400 }] });
    }
  };
  const setActionFile = (name: string) =>
    onChange({ action_list: [{ delay: 0, action: 'response_html', weight: 1, action_value: name }] });

  const setException = (val: string) =>
    onChange({
      exception_path: val,
      paths: val
        .split('\n')
        .map((p) => p.trim())
        .filter(Boolean),
    });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{meta.title}</CardTitle>
        <CardDescription>{meta.description}</CardDescription>
      </CardHeader>
      <CardBody className={styles.strategyBody}>
        <SettingRow
          title="Enable"
          description="Turn this emergency rule on for the selected websites."
          control={<Switch checked={s.enable} onCheckedChange={(v) => onChange({ enable: !!v })} />}
        />

        {!s.enable ? null : (
          <div className={styles.cardBody}>
          <div className={styles.sectionTitle}>Triggering Condition</div>

          {meta.showException && (
            <Labeled label="Exception Path" hint="Enter paths that will bypass this rule (one per line).">
              <Textarea
                value={s.exception_path ?? ''}
                placeholder="/path/to/bypass"
                onChange={(e) => setException(e.target.value)}
              />
            </Labeled>
          )}

          <Labeled
            label="Scheduling"
            required
            hint="Always: always effective · Once: effective once at the configured time · Periodic: effective on the configured weekdays and time."
          >
            <Select value={s.timer_type} onValueChange={(v) => onChange({ timer_type: v })}>
              <SelectTrigger className={styles.select320}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EMERGENCY_SCHEDULING.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Labeled>

          {s.timer_type === 'once' && (
            <DateTimeRangePicker
              value={{ from: s.timer_once?.[0] ?? '', to: s.timer_once?.[1] ?? '' }}
              onChange={setOnceRange}
              placeholder="Select the active window"
            />
          )}

          {s.timer_type === 'repeat' && (
            <div className={styles.stack3}>
              <div className={styles.daysRow}>
                {EMERGENCY_DAYS.map((d) => (
                  <label key={d.index} className={styles.dayLabel}>
                    <Checkbox checked={daySet.has(d.index)} onCheckedChange={() => toggleDay(d.index)} />
                    <span className={styles.smText}>{d.label}</span>
                  </label>
                ))}
              </div>
              <div className={styles.row2}>
                <Input
                  type="time"
                  step={1}
                  value={repeatLocalTime(s, 0)}
                  onChange={(e) => setRepeatTime('start', e.target.value)}
                  className={styles.input160}
                />
                <span>to</span>
                <Input
                  type="time"
                  step={1}
                  value={repeatLocalTime(s, 1)}
                  onChange={(e) => setRepeatTime('end', e.target.value)}
                  className={styles.input160}
                />
              </div>
            </div>
          )}

          {meta.showAction && (
            <>
              <div className={styles.sectionTitleGap}>Action</div>
              <Labeled
                label="Action"
                required
                hint="Block: intercept and return a 400 error code · Return HTML: intercept and return a pre-configured HTML page."
              >
                <div className={styles.rowWrap}>
                  <Select value={action} onValueChange={setAction}>
                    <SelectTrigger className={styles.select240}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EMERGENCY_ACTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {action === 'response_html' &&
                    (staticFiles.length === 0 ? (
                      <span className={styles.mutedSm}>
                        No uploaded HTML files — upload one in the portal first.
                      </span>
                    ) : (
                      <Select
                        value={typeof actionValue === 'string' ? actionValue : ''}
                        onValueChange={setActionFile}
                      >
                        <SelectTrigger className={styles.select240}>
                          <SelectValue placeholder="Select an HTML file" />
                        </SelectTrigger>
                        <SelectContent>
                          {staticFiles.map((f) => (
                            <SelectItem key={f.id} value={f.name}>
                              {f.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ))}
                </div>
              </Labeled>
            </>
          )}

          <Labeled label="Apply to" hint="Select the websites this strategy applies to.">
            <MultiSelect
              placeholder="Select websites"
              emptyHint="No websites available."
              options={websites.map((w) => ({ value: w.domain, label: w.domain }))}
              value={s.sites}
              onChange={(vals) => onChange({ sites: vals })}
            />
          </Labeled>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/** A small labelled field block (label + optional required mark + hint + control). */
function Labeled({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.labeled}>
      <div className={styles.labelText}>
        {label} {required && <span className={styles.required}>*</span>}
      </div>
      {hint && <div className={styles.mutedSm}>{hint}</div>}
      {children}
    </div>
  );
}
